import React, { useContext, useEffect, useMemo } from 'react';
import { Grid } from '@mui/material';
import { TranslationKey } from '../../../../../../../../types/translation/TranslationKey';
import {
  ProductTransaction,
  ProductTransactionStatus,
  ProductTransactionType,
} from '../../../../../../../../types/productTransaction';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../../types/container';
import { HandlingUnitContext } from '../../../../../../../../context/HandlingUnitContext';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';
import { HandlingUnit } from '../../../../../../../../types/handling_unit/handlingUnit';
import { t } from '../../../../../../../../types/translation/Translator';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';

interface ProductTransactionSourceHandlingUnitProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceHandlingUnit({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
}: ProductTransactionSourceHandlingUnitProps) {
  if (!productMasterData) return null;
  if (transaction.type === ProductTransactionType.inbound) return null;
  if (
    productMasterData.lotManaged ||
    productMasterData.serialManaged ||
    productMasterData.lpnManaged ||
    productMasterData.kitManaged
  ) {
    return null;
  }

  const { handlingUnits } = useContext(HandlingUnitContext);
  const { productMasterData: allProductMasterData } = useContext(ProductMasterDataContext);

  const items = useMemo(() => {
    if (!transaction.product.pmdId) return [];
    if (!transaction.product.fromStockLocationId) return [];

    return [...handlingUnits.values()].filter(item => item.productMasterDataId === transaction.product.pmdId);
  }, [handlingUnits, transaction.product.pmdId, transaction.product.fromStockLocationId]);

  const labelFor = (item: HandlingUnit) => {
    const parent = allProductMasterData.get(item.parentId);
    const parentName = parent?.productName || parent?.productNumber || item.parentId;
    return `${parentName}`;
  };

  const selected = items.find(item => item.id === transaction.product.handlingUnitId);

  useEffect(() => {
    if (!items.length) return;
    if (!transaction.product.handlingUnitId || selected) return;

    setTransaction(transaction => {
      transaction.product.handlingUnitId = undefined;
      return new ProductTransaction(transaction);
    });
  }, [items, selected, transaction.product.handlingUnitId, setTransaction]);

  if (!items.length) return null;

  return (
    <Grid item xs={6}>
      <DropdownSearchSelect<HandlingUnit>
        disabled={disabled || transaction.status === ProductTransactionStatus.picked}
        placeholder={t(TranslationKey.handlingUnit).singular.label}
        label={t(TranslationKey.handlingUnit).singular.label}
        items={items}
        value={selected}
        textBuilder={labelFor}
        searchBuilder={labelFor}
        onSelect={item => {
          if (transaction.product.handlingUnitId === item?.id) return;

          setTransaction(transaction => {
            transaction.product.handlingUnitId = item?.id;
            transaction.product.fromBinId = undefined;
            transaction.product.fromContainerId = undefined;
            transaction.product.fromCustomFields = new Map();
            return new ProductTransaction(transaction);
          });
        }}
      />
    </Grid>
  );
}
